import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import IconDone from '../../components/icons/IconDone';

const SKELETON_ROW_COUNT = 5;
const LIMIT = 15;

const parseIso = (value) => {
   if (!value) return null;
   const dt = DateTime.fromISO(String(value).replace(/\[.*\]/, '').replace(' ', 'T'));
   return dt.isValid ? dt : null;
};

function timeAgo(value, lang) {
   const then = parseIso(value);
   if (!then) return '';
   const minutes = Math.max(0, Math.round(DateTime.now().diff(then, 'minutes').minutes));
   if (minutes < 1) return lang.recentHomeworkJustNow;
   if (minutes < 60) return minutes + lang.minutesShort + ' ' + lang.agoSuffix;
   const hours = Math.round(minutes / 60);
   if (hours < 24) return hours + lang.hoursShort + ' ' + lang.agoSuffix;
   return Math.round(hours / 24) + lang.daysShort + ' ' + lang.agoSuffix;
}

const initials = (name, surname) => ((name || '').charAt(0) + (surname || '').charAt(0)).toUpperCase() || '?';

// Stable per-student hue so the same student always gets the same avatar
// colour across reloads (no stored colour anywhere).
const hueFor = (id) => (Number(id) * 47) % 360;

const formatLesson = (item) => {
   const day = DateTime.fromFormat(item.lessonDate || '', 'yyyy-MM-dd');
   const dayText = day.isValid ? day.toFormat('dd.MM') : (item.lessonDate || '');
   return dayText + (item.lessonStart ? ' · ' + item.lessonStart : '');
};

function RecentHomeworkRow({ item, lang }) {
   const navigate = useNavigate();
   const fullName = ((item.studentName || '') + ' ' + (item.studentSurname || '')).trim();
   const hue = hueFor(item.studentId);

   // Deep-link straight to the exercise's topic inside the course workspace.
   // courseId is the student's own course when they have one, else the
   // exercise's host course (homework picked from a course they aren't
   // enrolled in - their answers live in a courseless workspace, so the
   // course workspace may not exist yet). The workspace page dead-ends on
   // "student never opened this course" without one, so get-or-create it
   // first - same idempotent POST TopicCanvasView.jsx makes. No course at
   // all (homework-only exercise) -> student card, never a dead end.
   const open = () => {
      if (!item.courseId) {
         navigate('/students/' + item.studentId);
         return;
      }
      const target = '/students/' + item.studentId + '/workspace/' + item.courseId + (item.topicId ? '?topic=' + item.topicId : '');
      axios.post('/api/studentcourseworkspaces', JSON.stringify({ student: item.studentId, course: item.courseId }), {
         withCredentials: true, headers: { 'Content-Type': 'application/json' },
      }).catch(err => console.error(err)).finally(() => navigate(target));
   };

   return (
      <div className='recent-homework-row' onClick={open} title={item.courseId ? lang.recentHomeworkOpen : lang.recentHomeworkNoCourse}>
         <span className='recent-homework-avatar' style={{ '--rh-hue': hue }}>{initials(item.studentName, item.studentSurname)}</span>
         <div className='recent-homework-main'>
            <div className='recent-homework-top'>
               <span className='recent-homework-student'>{fullName}</span>
               <span className='recent-homework-time'>{timeAgo(item.completedAt, lang)}</span>
            </div>
            <p className='recent-homework-exercise'>{item.exerciseName || item.homeworkText}</p>
            <div className='recent-homework-meta'>
               <span className='recent-homework-chip recent-homework-chip-lesson'>{lang.recentHomeworkLesson} {formatLesson(item)}</span>
               {item.elementsTotal ? (
                  <span className='recent-homework-chip recent-homework-chip-done'>
                     <IconDone fillColor='icon-green' />{item.elementsTotal} {lang.recentHomeworkTasks}
                  </span>
               ) : ''}
               {item.score != null ? <span className='recent-homework-chip'>{lang.recentHomeworkScore}: {Math.round(item.score * 10) / 10}</span> : ''}
            </div>
         </div>
      </div>
   );
}

// Dashboard tile that replaced LastConversationProgress (bot conversations,
// still in the codebase, just no longer mounted): the teacher's "what did
// students just finish" feed. One row per (homework, student) as computed by
// GET /api/dashboard/completed-homework, click jumps into that student's
// course workspace (the teacher's read-only review of their answers).
export default function RecentHomework() {
   const lang = language[language.lang];
   const [data, setData] = useState([]);
   const [status, setStatus] = useState('loading');

   useEffect(() => {
      axios.get('/api/dashboard/completed-homework?limit=' + LIMIT, { withCredentials: true })
         .then(response => setData(Array.isArray(response.data) ? response.data : []))
         .catch(err => console.error(err))
         .finally(() => setStatus('done'));
   }, []);

   return (
      <div className='recent-homework-panel dashboard-component'>
         <div className='header-dahs-elem'>
            <IconDone style={{ width: 30, height: 30, fill: 'var(--dashboard-header-icon-color)' }} />
            <p className='txt-c'>{lang.recentHomeworkTitle}</p>
         </div>
         <div className='recent-homework-list scrooll'>
            {status === 'loading' ? (
               Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                  <div key={i} className='skeleton-block recent-homework-skeleton'></div>
               ))
            ) : ''}
            {status === 'done' && data.length === 0 ? <p className='pending-approvals-empty'>{lang.recentHomeworkEmpty}</p> : ''}
            {status === 'done' ? data.map(item => <RecentHomeworkRow key={item.homeworkId + '-' + item.studentId} item={item} lang={lang} />) : ''}
         </div>
      </div>
   );
}
